import { PRODUCT_VERSION_2026 } from "./constants";
import type {
  OptimisticAccessProduct,
  OptimisticBillingEvent,
} from "./commandTypes";
import type { BillingSnapshot, RawSubscriptionAccess } from "./types";

type ActiveSubscription =
  RawSubscriptionAccess["subscriptions"]["active"][number];

type AccessProduct = RawSubscriptionAccess["access"]["products"][number];

const sameIds = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((id) => right.includes(id));

const mergeProducts = (
  existing: readonly AccessProduct[],
  targetEntitlements: readonly string[],
  optimistic: readonly OptimisticAccessProduct[]
): AccessProduct[] => {
  const targetKeys = new Set(targetEntitlements);
  const optimisticProductIds = new Set(
    optimistic.map(({ stripeProductId }) => stripeProductId)
  );
  const retained = existing.filter(
    ({ entitlementProfileKey, stripeProductId }) =>
      !optimisticProductIds.has(stripeProductId) &&
      (entitlementProfileKey == null || targetKeys.has(entitlementProfileKey))
  );

  return [
    ...retained,
    ...optimistic.map((product) => ({
      ...product,
      priceIds: [...product.priceIds],
    })),
  ];
};

const productIdsForPrices = (
  products: readonly AccessProduct[],
  priceIds: readonly string[]
): string[] =>
  products
    .filter((product) =>
      product.priceIds.some((priceId) => priceIds.includes(priceId))
    )
    .map(({ stripeProductId }) => stripeProductId);

const updateSubscription = (
  snapshot: BillingSnapshot,
  subscriptionId: string,
  update: (subscription: ActiveSubscription) => ActiveSubscription
): BillingSnapshot => ({
  ...snapshot,
  subscriptionAccessData: {
    ...snapshot.subscriptionAccessData,
    subscriptions: {
      ...snapshot.subscriptionAccessData.subscriptions,
      active: snapshot.subscriptionAccessData.subscriptions.active.map(
        (subscription) =>
          subscription.stripeSubscriptionId === subscriptionId
            ? update(subscription)
            : subscription
      ),
    },
  },
});

const applyCreate = (
  snapshot: BillingSnapshot,
  event: Extract<OptimisticBillingEvent, { type: "create-subscription" }>
): BillingSnapshot => {
  const products = mergeProducts(
    snapshot.subscriptionAccessData.access.products,
    event.targetEntitlementProfileKeys,
    event.products
  );
  const periodSeconds = event.cadence === "year" ? 365 * 86_400 : 30 * 86_400;
  const subscription: ActiveSubscription = {
    stripeSubscriptionId: `optimistic-${event.createdAt}`,
    status: "active",
    interval: event.cadence,
    intervalCount: 1,
    startDate: event.createdAt,
    periodStart: event.createdAt,
    periodEnd: event.createdAt + periodSeconds,
    cancelAt: null,
    pauseAt: null,
    pendingPauseCommandId: null,
    pendingPauseEffectiveAt: null,
    trialEnd: null,
    stripePriceId: event.targetPriceIds[0] ?? "",
    priceIds: [...event.targetPriceIds],
    productIds: productIdsForPrices(products, event.targetPriceIds),
    version: PRODUCT_VERSION_2026,
  };

  return {
    ...snapshot,
    subscriptionAccessData: {
      ...snapshot.subscriptionAccessData,
      access: {
        ...snapshot.subscriptionAccessData.access,
        entitlementProfileKeys: [...event.targetEntitlementProfileKeys],
        products,
      },
      subscriptions: {
        ...snapshot.subscriptionAccessData.subscriptions,
        active: [
          ...snapshot.subscriptionAccessData.subscriptions.active,
          subscription,
        ],
      },
    },
  };
};

const applyProductSetChange = (
  snapshot: BillingSnapshot,
  event: Extract<OptimisticBillingEvent, { type: "change-product-set" }>
): BillingSnapshot => {
  const billedPriceIds = new Set([
    ...event.targetPriceIds,
    ...snapshot.subscriptionAccessData.subscriptions.active
      .filter(
        ({ stripeSubscriptionId }) =>
          stripeSubscriptionId !== event.stripeSubscriptionId
      )
      .flatMap(({ priceIds }) => priceIds),
  ]);
  // A product without an entitlement key (the Pause Plan, say) is known only by the prices
  // it is billed on, so it leaves the overlay once no subscription bills any of them.
  const products = mergeProducts(
    snapshot.subscriptionAccessData.access.products,
    event.targetEntitlementProfileKeys,
    event.products
  ).filter(
    ({ entitlementProfileKey, priceIds }) =>
      entitlementProfileKey != null ||
      priceIds.some((priceId) => billedPriceIds.has(priceId))
  );
  const productIds = productIdsForPrices(products, event.targetPriceIds);

  return {
    ...snapshot,
    subscriptionAccessData: {
      ...snapshot.subscriptionAccessData,
      access: {
        ...snapshot.subscriptionAccessData.access,
        entitlementProfileKeys: [...event.targetEntitlementProfileKeys],
        products,
      },
      subscriptions: {
        ...snapshot.subscriptionAccessData.subscriptions,
        active: snapshot.subscriptionAccessData.subscriptions.active.map(
          (subscription) =>
            subscription.stripeSubscriptionId === event.stripeSubscriptionId
              ? {
                  ...subscription,
                  priceIds: [...event.targetPriceIds],
                  stripePriceId:
                    event.targetPriceIds[0] ?? subscription.stripePriceId,
                  productIds,
                }
              : subscription
        ),
      },
    },
  };
};

export const applyOptimisticEvent = (
  snapshot: BillingSnapshot,
  event: OptimisticBillingEvent
): BillingSnapshot => {
  switch (event.type) {
    case "create-subscription":
      return applyCreate(snapshot, event);
    case "change-product-set":
      return applyProductSetChange(snapshot, event);
    case "cancel-subscription":
      return updateSubscription(
        snapshot,
        event.stripeSubscriptionId,
        (subscription) => ({ ...subscription, cancelAt: event.cancelAt })
      );
    case "pause-subscription":
      return updateSubscription(
        snapshot,
        event.stripeSubscriptionId,
        (subscription) => ({ ...subscription, pauseAt: event.pauseAt })
      );
    case "cancel-pending-pause":
      return updateSubscription(
        snapshot,
        event.stripeSubscriptionId,
        (subscription) => ({
          ...subscription,
          pauseAt: null,
          pendingPauseCommandId: null,
          pendingPauseEffectiveAt: null,
        })
      );
    case "release-subscription":
      return updateSubscription(
        snapshot,
        event.stripeSubscriptionId,
        (subscription) => ({ ...subscription, cancelAt: null })
      );
    case "cancel-pending-module-removal":
      return {
        ...snapshot,
        subscriptionAccessData: {
          ...snapshot.subscriptionAccessData,
          scheduledModuleRemovals: (
            snapshot.subscriptionAccessData.scheduledModuleRemovals ?? []
          ).filter(({ commandId }) => commandId !== event.commandId),
        },
      };
    // A commitment changes nothing the merchant holds until it applies.
    case "commit-trial-plan":
      return snapshot;
    case "apply-coupon":
      return updateSubscription(
        snapshot,
        event.stripeSubscriptionId,
        (subscription) => ({
          ...subscription,
          operatorCoupons: [
            ...(subscription.operatorCoupons ?? []),
            {
              id: event.couponId,
              percentOff: event.percentOff,
              amountOff: event.amountOff,
            },
          ],
        })
      );
  }
};

const entitlementChangesConfirmed = (
  snapshot: BillingSnapshot,
  event: Extract<
    OptimisticBillingEvent,
    { type: "create-subscription" | "change-product-set" }
  >
): boolean => {
  const { access } = snapshot.subscriptionAccessData;
  const activeKeys = new Set(access.entitlementProfileKeys);
  const addedKeysConfirmed = event.addedEntitlementKeySets.every((keys) =>
    keys.some((key) => activeKeys.has(key))
  );
  const removedKeys = new Set(event.removedEntitlementKeySets.flat());
  const removedKeysAbsent = [...removedKeys].every(
    (key) => !activeKeys.has(key)
  );
  const removedProductsAbsent = access.products.every(
    ({ entitlementProfileKey }) =>
      entitlementProfileKey == null || !removedKeys.has(entitlementProfileKey)
  );

  return addedKeysConfirmed && removedKeysAbsent && removedProductsAbsent;
};

const addedProductsConfirmed = (
  snapshot: BillingSnapshot,
  event: Extract<
    OptimisticBillingEvent,
    { type: "create-subscription" | "change-product-set" }
  >,
  subscription: ActiveSubscription
): boolean =>
  event.products.every((expected) => {
    const product = snapshot.subscriptionAccessData.access.products.find(
      ({ stripeProductId }) => stripeProductId === expected.stripeProductId
    );

    if (product?.entitlementProfileKey == null) {
      return false;
    }

    const belongsToAddedEntity = event.addedEntitlementKeySets.some((keys) =>
      keys.includes(product.entitlementProfileKey!)
    );

    return (
      belongsToAddedEntity &&
      snapshot.subscriptionAccessData.access.entitlementProfileKeys.includes(
        product.entitlementProfileKey
      ) &&
      product.priceIds.some((priceId) =>
        subscription.priceIds.includes(priceId)
      )
    );
  });

/**
 * A plan intent parked in `pending_capacity` is still waiting on a Shopify cap
 * approval nobody has granted yet. The snapshot can already carry the target
 * prices and entitlements from the bridge subscription the backend staged, so
 * confirming off those alone would report a change the merchant has not been
 * given: the approval, not the snapshot, owns the outcome until it clears.
 */
const awaitsShopifyCapApproval = (snapshot: BillingSnapshot): boolean =>
  snapshot.subscriptionAccessData.planIntent?.state === "pending_capacity";

export const isOptimisticEventConfirmed = (
  snapshot: BillingSnapshot,
  event: OptimisticBillingEvent
): boolean => {
  const subscriptions = snapshot.subscriptionAccessData.subscriptions.active;

  switch (event.type) {
    case "create-subscription": {
      if (awaitsShopifyCapApproval(snapshot)) {
        return false;
      }
      const subscription = subscriptions.find(({ priceIds }) =>
        sameIds(priceIds, event.targetPriceIds)
      );
      return (
        subscription !== undefined &&
        entitlementChangesConfirmed(snapshot, event) &&
        addedProductsConfirmed(snapshot, event, subscription)
      );
    }
    case "change-product-set": {
      if (awaitsShopifyCapApproval(snapshot)) {
        return false;
      }
      const subscription = subscriptions.find(
        ({ stripeSubscriptionId }) =>
          stripeSubscriptionId === event.stripeSubscriptionId
      );
      return (
        subscription !== undefined &&
        sameIds(subscription.priceIds, event.targetPriceIds) &&
        entitlementChangesConfirmed(snapshot, event) &&
        addedProductsConfirmed(snapshot, event, subscription)
      );
    }
    case "cancel-subscription":
      return subscriptions.some(
        ({ cancelAt, stripeSubscriptionId }) =>
          stripeSubscriptionId === event.stripeSubscriptionId &&
          cancelAt === event.cancelAt
      );
    case "pause-subscription":
      return subscriptions.some(
        ({ pauseAt, stripeSubscriptionId }) =>
          stripeSubscriptionId === event.stripeSubscriptionId &&
          pauseAt === event.pauseAt
      );
    case "cancel-pending-pause":
      return !subscriptions.some(
        ({ pendingPauseCommandId }) => pendingPauseCommandId === event.commandId
      );
    case "release-subscription":
      return subscriptions.some(
        ({ cancelAt, stripeSubscriptionId }) =>
          stripeSubscriptionId === event.stripeSubscriptionId &&
          cancelAt == null
      );
    case "apply-coupon":
      return subscriptions.some(
        ({ operatorCoupons, stripeSubscriptionId }) =>
          stripeSubscriptionId === event.stripeSubscriptionId &&
          (operatorCoupons ?? []).some(({ id }) => id === event.couponId)
      );
    case "cancel-pending-module-removal":
      return !(
        snapshot.subscriptionAccessData.scheduledModuleRemovals ?? []
      ).some(({ commandId }) => commandId === event.commandId);
    case "commit-trial-plan": {
      const { planCommitment } = snapshot.subscriptionAccessData;
      if (planCommitment) {
        return sameIds(planCommitment.priceIds, event.targetPriceIds);
      }
      // Once applied, a plan that ended the trial is no commitment any more:
      // it is what the subscription bills.
      return (
        event.endsTrial &&
        subscriptions.some(
          ({ priceIds, status }) =>
            status !== "trialing" && sameIds(priceIds, event.targetPriceIds)
        )
      );
    }
  }
};
